import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { onboardingApi } from '../services/onboardingService'
import { transformOnboardingData } from '../utils/onboardingTransformers'
import { UseOnboardingReturn } from '../types/onboarding.types'

/**
 * Hook para obtener y procesar los datos de onboarding desde Contentful
 * Utiliza Tanstack Query para cache de 5 días y mejores prácticas
 *
 * @returns {UseOnboardingReturn} Objeto con loading, error y datos procesados
 */
export const useOnboarding = (): UseOnboardingReturn => {
	// Query con Tanstack Query
	const {
		data: rawData,
		isLoading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'onboarding'] as const,
		queryFn: onboardingApi.getOnboarding,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true
	})

	// Manejar errores de manera moderna
	if (error) {
		console.error('Error al obtener datos de onboarding desde Contentful:', error)
	}

	// Procesar datos usando memoización para evitar recalculos innecesarios
	const processedData = useMemo(() => {
		if (!rawData || isLoading || error) {
			return {
				onboardingData: false,
				textAlert: {
					success: 'Se ha actualizado tus datos.',
					error: 'No se pudo guardar los cambios'
				},
				beneficioItems: []
			}
		}

		try {
			return transformOnboardingData(rawData)
		} catch (transformError) {
			console.error('Error al transformar datos de onboarding:', transformError)
			return {
				onboardingData: false,
				textAlert: {
					success: 'Se ha actualizado tus datos.',
					error: 'No se pudo guardar los cambios'
				},
				beneficioItems: []
			}
		}
	}, [rawData, isLoading, error])

	// Información adicional sobre el estado del cache
	const cacheInfo = useMemo(
		() => ({
			isStale,
			isRefetching,
			lastFetch: rawData ? new Date() : null,
			cacheExpiry: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000) // 5 días desde ahora
		}),
		[isStale, isRefetching, rawData]
	)

	return {
		// Datos principales (manteniendo compatibilidad con el hook original)
		loading: isLoading,
		error: error as Error | null,
		isTextAlert: processedData.textAlert,
		isOnboardingData: processedData.beneficioItems,

		// Funcionalidades adicionales de Tanstack Query
		refetch,
		isStale,
		isRefetching,
		cacheInfo,

		// Datos raw por si se necesitan
		rawData
	}
}

export default useOnboarding
